import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { getVideoLanguage } from 'js/src/data/app/app-selectors';
import { getLanguageReadingSpeed } from 'js/src/data/translations';
import { SCENE_DURATIONS } from 'js/src/libs/consts';
import {
    _calculateTextDurationInMs,
    calculateSceneDuration,
    defaultLanguageReadingSpeed,
} from 'js/src/libs/timing-utils';
import {
    getEditableSubslideText,
    getRevealedTextAssets,
    getSceneGroupCategory,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';

/**
 * How far below the recommended reading time a scene must fall before we warn.
 * Small overshoots are common and not worth flagging.
 */
const READING_TIME_MARGIN_MS = 500;

/**
 * The least screen time a revealed text asset needs to register at all, however short its text.
 * The scene-level bar gets this from `calculateSceneDuration`'s `SCENE_DURATIONS.MIN`, which is far
 * too high for a single revealed line; this matches the voice-driven check's floor instead.
 */
const REVEAL_FLOOR_MS = 1000;

const seconds = (ms: number): string => (ms / 1000).toFixed(1);

/**
 * Warning: a text-on-media scene is meaningfully shorter than the time its
 * on-screen text needs to be read comfortably. Voice-driven scene groups are
 * skipped (their timing is driven by the voiceover, not reading speed).
 * Warning only — the user may have deliberately shortened the scene.
 *
 * The same bar is held per text asset that is revealed partway through the scene
 * (`set_text` with `start_time_ms`, or a design's stagger): a scene long enough
 * for all its text still fails a line that only lands in its last half second.
 */
export function checkSceneReadingTime(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];
    const state = getState();
    const videoLanguage = state.app ? getVideoLanguage(state) : null;
    const readingSpeed = getLanguageReadingSpeed(state, videoLanguage);
    const paceMultiplier = luminary.paceMultiplier;

    luminary.scenes.forEach((scene) => {
        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup || isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        const sceneTexts = scene.subscenes.map(getEditableSubslideText);
        if (sceneTexts.join('').trim().length === 0) {
            return;
        }

        const sceneCategory = getSceneGroupCategory(sceneGroup);
        const recommendedDuration = calculateSceneDuration(
            sceneTexts,
            sceneCategory,
            paceMultiplier,
            readingSpeed ?? undefined,
        );

        if (scene.duration < recommendedDuration - READING_TIME_MARGIN_MS) {
            const actualSeconds = seconds(scene.duration);
            const recommendedSeconds = seconds(recommendedDuration);
            findings.push({
                check: 'scene_reading_time',
                severity: 'warning',
                sceneId: scene.id,
                message: `Scene is ${actualSeconds}s but its on-screen text needs about ${recommendedSeconds}s to read comfortably. This may be intentional — confirm the scene isn't too short.`,
            });
        }

        // A text asset revealed partway through is only readable for what is left of the scene.
        // The bar is clamped the way the scene-level one is: never above SCENE_DURATIONS.MAX (the
        // scene itself is capped there, so a longer requirement could never be met by revealing
        // earlier) and never below a floor that even a two-word line needs to register.
        getRevealedTextAssets(scene).forEach(({ asset, introMs, containerDurationMs }) => {
            const visibleMs = containerDurationMs - introMs;
            const rawRequiredMs = _calculateTextDurationInMs(
                asset.text,
                paceMultiplier,
                readingSpeed ?? defaultLanguageReadingSpeed,
            );
            const requiredMs = Math.min(
                Math.max(rawRequiredMs, REVEAL_FLOOR_MS),
                SCENE_DURATIONS.MAX,
            );
            if (visibleMs < requiredMs - READING_TIME_MARGIN_MS) {
                findings.push({
                    check: 'scene_reading_time',
                    severity: 'warning',
                    sceneId: scene.id,
                    message: `"${asset.name}" appears ${seconds(introMs)}s into the scene and is on screen for only ${seconds(visibleMs)}s, but its text needs about ${seconds(requiredMs)}s to read comfortably. Reveal it earlier or lengthen the scene.`,
                });
            }
        });
    });

    return findings;
}
